import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import Navbar from "../components/Navbar";
import TourManagementOptionsSidebar from "../components/tour/TourManagementSidebarOptionsSidebar";
import CustomRadio from "../components/common/CustomRadio";
import { Paths } from "../router/Paths";
import useOnLoadPage from "../hooks/useOnLoadPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setPackageData, setOptionsData } from "../reducer/tourReducer";
import { usePackage } from "../hooks/usePackage";
import { useOptions } from "../hooks/useOptions";
import { resetOptionEdit } from "../reducer/tourEditReducer";

const languages = [
  "English",
  "Pidgin English",
  "Ibo",
  "Yoruba",
  "Hausa",
  "French",
  "Portugese",
  "Spanish",
  "Italian",
  "Russian",
  "Chinese",
  "German",
];

const customerSkipLine = [
  "Skip the line to get tickets",
  "Skip the line throught a separate entrance",
  "Skip the line throught expresss security check",
  "Skip the line through express securiy check",
  "skip the line through express elevators",
];

const timeOptions = ["Minute(s)", "Hour(s)", "Day(s)"];
const cutoffOptions = ["30m - 1 hour", "3 hours", "1 day", "2 days", "3 days"];

const validity = [
  "Valid for a period of time from date booked",
  "Valid for a period of time from first activation",
  "Valid on the date booked",
];

function TourManagementPackageOptionsSetupPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { packageData, optionsData, packageId } = useSelector(
    (state: RootState) => state.tour
  );
  const {
    tourEdit,
    optionId,
    optionEdit,
    id: editPackageId,
  } = useSelector((state: RootState) => state.edit);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [timeOption, setTimeOption] = useState<string>("");
  const [durationValue, setDurationValue] = useState("");
  const [loading, setLoading] = useState(false);
  const { createPreOptions } = useOptions();
  const { PrepackageDataId } = useSelector((state: RootState) => state.tour);
  const progressref = useRef(11);

  useEffect(() => {
    if (location.state && location.state.optionData) {
      dispatch(setOptionsData(location.state.optionData));
    }
  }, [location.state, dispatch]);

  const [editedTitle, setEditedTitle] = useState<string>(
    optionsData?.title || ""
  );
  const [editedReferenceCode, setEditedReferenceCode] = useState<string>(
    optionsData?.referenceCode || ""
  );
  const [editedDescription, setEditedDescription] = useState<string>(
    optionsData?.description || ""
  );
  const [editedGroupSize, setEditedGroupSize] = useState<number>(
    optionsData?.maxGroupSize || 0
  );
  const [editedGuideLanguages, setEditedGuideLanguages] = useState<string[]>(
    optionsData?.guideLanguages || []
  );
  const [editedIsPrivate, setEditedIsPrivate] = useState<boolean>(
    Boolean(optionsData?.isPrivateActivity) || false
  );
  const [editedGuideMaterials, setEditedGuideMaterials] = useState<string>(
    optionsData?.guideMaterial || ""
  );
  const [editedCutoff, setEditedCutoff] = useState<string>(
    optionsData?.cutOffTime || ""
  );
  const [editedCustomerLineToSkip, setEditedCustomerLineToSkip] =
    useState<boolean>(Boolean(optionsData?.customerSkipLine) || false);

  const [editedLineToSkip, setEditedLineToSkip] = useState<string>(
    optionsData?.lineToSkip || ""
  );

  const [editedWheelchair, setEditedWheelchair] = useState<boolean>(
    optionsData?.wheelchair || false
  );

  const [editedAccessTime, setEditedAccessTime] = useState<string>(
    optionsData?.accessTime || ""
  );

  useOnLoadPage({ tabActive: "create" });

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!editedTitle.trim()) {
      newErrors.optionTitle = "Title is required.";
    }

    if (!editedGroupSize || editedGroupSize <= 0) {
      newErrors.groupSize = "Please provide a valid group size.";
    }

    if (!editedDescription.trim()) {
      newErrors.optionDescription = "Description is required.";
    }

    if (!editedGuideLanguages.length) {
      newErrors.guideLanguages = "Please select at least one guide language.";
    }

    if (editedCustomerLineToSkip && !editedLineToSkip.trim()) {
      newErrors.lineToSkip = "Customer skip line information is required.";
    }

    if (editedWheelchair === undefined) {
      newErrors.wheelchair =
        "Please specify if the activity is wheelchair accessible.";
    }

    if (!editedCutoff.trim()) {
      newErrors.cuttoff = "Cut-off time is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const errorStyle = (field: string) =>
    errors[field] ? "border border-red-500" : "";

  const onContinue = () => {
    if (validate()) {
      console.log(packageData);
      navigate(Paths.tourManagementPackageOptionsMeeting);
    }
  };

  const onSave = async () => {
    if (!validate()) {
      console.error("Validation failed. Fix errors before saving.");
      return;
    }

    const updatedPackageData: any = {
      ...optionsData,
      progress: Number(progressref.current),
      title: editedTitle,
      description: editedDescription,
      maxGroupSize: editedGroupSize,
      guideLanguages: editedGuideLanguages,
      guideMaterial: editedGuideMaterials,
      isPrivateActivity: editedIsPrivate,
      customerSkipLine: editedCustomerLineToSkip,
      lineToSkip: editedLineToSkip,
      wheelchair: editedWheelchair,
      accessTime: editedAccessTime,
      cutOffTime: editedCutoff,
    };

    if (optionsData?.referenceCode) {
      updatedPackageData.referenceCode = editedReferenceCode;
    }

    const updatedPackageData2 = {
      ...optionsData,
      ...updatedPackageData,
    };

    // Remove _id if present to ensure API creates a new option
    if (updatedPackageData2._id) {
      console.log("Removing _id before API call:", updatedPackageData2._id);
      if (!optionEdit) delete updatedPackageData2._id;
    }

    setLoading(true);

    try {
      if (tourEdit) {
        dispatch(resetOptionEdit());
        dispatch(setOptionsData(updatedPackageData2));
        const pkgId = packageData._id;
        navigate(`/manage/product/${pkgId}/${optionId}`, {
          state: {
            fromEdit: true,
            option: updatedPackageData2,
            packageId: pkgId,
          },
        });
        return;
      } else {
        await createPreOptions(updatedPackageData2);
      }
      navigate(Paths.welcome);
    } catch (error) {
      console.error("Error saving package:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLanguage = () => {
    if (!selectedLanguage) return;
    let data = editedGuideLanguages.includes(selectedLanguage)
      ? editedGuideLanguages
      : [...editedGuideLanguages, selectedLanguage];
    setEditedGuideLanguages(data);
    dispatch(
      setOptionsData({
        ...optionsData,
        guideLanguages: data,
      })
    );
    setSelectedLanguage("");
  };

  const handleRemoveLanguage = (index: number) => {
    let filteredData = editedGuideLanguages.filter((_, idx) => idx !== index);
    setEditedGuideLanguages(filteredData);
    dispatch(
      setOptionsData({
        ...optionsData,
        guideLanguages: filteredData,
      })
    );
  };

  useEffect(() => {
    if (editedAccessTime) {
      const [duration, option] = editedAccessTime.split(" ");
      setDurationValue(duration || "");
      setTimeOption(option || "");
    }
    if (optionsData.cutOffTime) {
      const matchedCutoff = cutoffOptions.find(
        (option) => option === optionsData.cutOffTime
      );
      if (matchedCutoff) {
        setEditedCutoff(matchedCutoff);
      } else {
        console.warn(
          "No matching cutoff time found for:",
          optionsData.cutOffTime
        );
      }
    }
    if (optionsData?.guideLanguages) {
      setEditedGuideLanguages(optionsData.guideLanguages);
    }
    if (optionsData.lineToSkip) {
      const matchedLine = customerSkipLine.find(
        (line) => line === optionsData.lineToSkip
      );
      if (matchedLine) {
        setEditedLineToSkip(matchedLine); // Synchronize editedLineToSkip with the matched value
      }
    }
  }, [
    optionsData.accessTime,
    optionsData.cutOffTime,
    optionsData.lineToSkip,
    optionsData.guideLanguages,
  ]);

  useEffect(() => {
    if (typeof optionsData?.isPrivateActivity === "undefined") {
      dispatch(
        setOptionsData({
          ...optionsData,
          isPrivateActivity: false,
        })
      );
    }
    if (typeof optionsData?.wheelchair === "undefined") {
      dispatch(
        setOptionsData({
          ...optionsData,
          wheelchair: false,
        })
      );
    }
    if (typeof optionsData?.customerSkipLine === "undefined") {
      dispatch(
        setOptionsData({
          ...optionsData,
          customerSkipLine: false,
        })
      );
    }
  }, []);

  console.log("package options data", packageData);
  console.log("package id in options page", packageId);
  console.log("options data in options page", optionsData);
  console.log("edited group Size", editedGroupSize);

  return (
    <div className={Classname.container}>
      <Navbar />
      <div className={Classname.content}>
        <TourManagementOptionsSidebar selected={1} />
        <div className={Classname.detail}>
          <div className={Classname.title}>Option Title</div>
          <div className={Classname.description}>
            if you offer multiple options, write a short title that clearly
            explains to the customer how this options differs from the others.
          </div>
          <input
            className={Classname.input}
            placeholder="Enter Title"
            value={editedTitle}
            onChange={(e) => {
              setEditedTitle(e.target.value);
              dispatch(
                setOptionsData({
                  ...optionsData,
                  title: e.target.value,
                })
              );
            }}
          />
          {errors.optionTitle && (
            <div className="text-red-500">{errors.optionTitle}</div>
          )}
          <div className="flex gap-1 mt-5">
            <div className="font-bold">Option reference code </div>
            <span> (optional)</span>
          </div>
          <div className={Classname.description}>
            Provide a reference code to help you keep track of which option the
            customer has booked. This is for your own records and won’t be seen
            by the customer.
          </div>
          <input
            className={Classname.input}
            placeholder="Enter reference code"
            value={editedReferenceCode}
            onChange={(e) => {
              setEditedReferenceCode(e.target.value);
              dispatch(
                setOptionsData({
                  ...optionsData,
                  referenceCode: e.target.value,
                })
              );
            }}
          />

          <span className={Classname.title}>Option description</span>
          <p className={Classname.description}>
            Now, provide option description which help to clarify a detail to
            the customer
          </p>
          <textarea
            name=""
            id=""
            cols={30}
            rows={5}
            className={Classname.textarea}
            placeholder="Enter your details"
            value={editedDescription}
            onChange={(e) => {
              setEditedDescription(e.target.value);
              dispatch(
                setOptionsData({
                  ...optionsData,
                  description: e.target.value,
                })
              );
            }}
          />
          {errors.description && (
            <div className="text-red-500">{errors.description}</div>
          )}
          {/* max group size */}
          <div className="font-semibold mt-2">Maximum group size</div>
          <div className={Classname.description}>
            What is the maximum total of people in your experience? This number
            includes people who book outside of Intraverse.
          </div>
          <div className=" mb-4">
            <input
              type="number"
              min={0}
              className={Classname.input}
              placeholder="Enter maximum group size"
              value={editedGroupSize}
              onChange={(e) => {
                setEditedGroupSize(Math.abs(parseInt(e.target.value)));
                dispatch(
                  setOptionsData({
                    ...optionsData,
                    maxGroupSize: Math.abs(parseInt(e.target.value)),
                  })
                );
              }}
            />
            {errors.groupSize && (
              <div className="text-red-500">{errors.groupSize}</div>
            )}
          </div>
          <div className={Classname.title}>Option setup</div>
          <div className="font-bold my-3">
            What languages does the guide speak during the activity? Choose all
            that apply.
          </div>
          <div className="flex gap-4">
            <div className="flex flex-1">
              <FormControl fullWidth>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedLanguage}
                  className="mt-2"
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddLanguage();
                    }
                  }}
                >
                  {languages.map((language, index) => (
                    <MenuItem value={language} key={index}>
                      {language}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <button className={Classname.addButton} onClick={handleAddLanguage}>
              + Add
            </button>
          </div>
          <div className={Classname.listContainer}>
            {editedGuideLanguages.map((kword, index) => (
              <div
                className={Classname.listItem}
                key={index}
                onClick={() => handleRemoveLanguage(index)}
              >
                <span>{kword}</span>
                <img
                  src="/IconMinus.svg"
                  alt=""
                  className="h-5 w-5 object-contain"
                />
              </div>
            ))}
          </div>
          {errors.guideLanguages && (
            <div className="text-red-500 mt-2">{errors.guideLanguages}</div>
          )}

          <div className="font-semibold mt-2">Is this a private activity?</div>
          <div className={Classname.description}>
            This means that only one group or person can participate. There
            won’t be other customers in the same activity.
          </div>
          <CustomRadio
            label="No"
            value={false}
            selected={editedIsPrivate}
            onSelect={() => {
              setEditedIsPrivate(false);
              dispatch(
                setOptionsData({
                  ...optionsData,
                  isPrivateActivity: false,
                })
              );
            }}
          />
          <CustomRadio
            label="Yes"
            value={true}
            selected={editedIsPrivate}
            onSelect={() => {
              setEditedIsPrivate(true);
              dispatch(
                setOptionsData({
                  ...optionsData,
                  isPrivateActivity: true,
                })
              );
            }}
          />
          {errors.isPrivate && (
            <div className="text-red-500">{errors.isPrivate}</div>
          )}

          <div className={Classname.title}>
            What guide materials do you provide in which languages? Choose all
            that apply. (optional)
          </div>
          <CustomRadio
            label="Audio guides and headphones"
            value="Audio guides and headphones"
            selected={editedGuideMaterials}
            onSelect={() => {
              setEditedGuideMaterials("Audio guides and headphones");
              dispatch(
                setOptionsData({
                  ...optionsData,
                  guideMaterial: "Audio guides and headphones",
                })
              );
            }}
          />
          <CustomRadio
            label="Information booklets"
            value="Information booklets"
            selected={editedGuideMaterials}
            onSelect={() => {
              setEditedGuideMaterials("Information booklets");
              dispatch(
                setOptionsData({
                  ...optionsData,
                  guideMaterial: "Information booklets",
                })
              );
            }}
          />

          {/* will customer skip */}
          <div className={Classname.title}>
            Will the customer skip the line to get in?
          </div>
          <CustomRadio
            label="No"
            value={false}
            selected={editedCustomerLineToSkip}
            onSelect={() => {
              setEditedCustomerLineToSkip(false);
              dispatch(
                setOptionsData({ ...optionsData, customerSkipLine: false })
              );
            }}
          />
          <CustomRadio
            value={true}
            label="Yes"
            selected={editedCustomerLineToSkip}
            onSelect={() => {
              setEditedCustomerLineToSkip(true);
              dispatch(
                setOptionsData({ ...optionsData, customerSkipLine: true })
              );
            }}
          />
          {editedCustomerLineToSkip && (
            <>
              <div className={Classname.title}>Customer skip line</div>
              <div className="flex flex-1">
                <FormControl fullWidth>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={editedLineToSkip}
                    className="mt-2"
                    onChange={(e) => {
                      setEditedLineToSkip(e.target.value);
                      dispatch(
                        setOptionsData({
                          ...optionsData,
                          lineToSkip: e.target.value,
                        })
                      );
                    }}
                  >
                    {customerSkipLine.map((skipline: string, index: number) => (
                      <MenuItem value={skipline} key={index}>
                        {skipline}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </>
          )}

          {errors.lineToSkip && (
            <div className="error text-red-500">{errors.lineToSkip}</div>
          )}
          {/* wheelchair accessible */}
          <div className={Classname.title}>
            Is the activity wheelchair accessible?
          </div>
          <CustomRadio
            label="No"
            value={false}
            selected={editedWheelchair}
            onSelect={() => {
              setEditedWheelchair(false);
              dispatch(setOptionsData({ ...optionsData, wheelchair: false }));
            }}
          />
          <CustomRadio
            label="Yes"
            value={true}
            selected={editedWheelchair}
            onSelect={() => {
              setEditedWheelchair(true);
              dispatch(setOptionsData({ ...optionsData, wheelchair: true }));
            }}
          />
          {errors.wheelchair && (
            <div className="text-red-500">{errors.wheelchair}</div>
          )}
          <div className={Classname.title}>Duration or validity</div>

          <div className={Classname.description}>
            Some activities start and stop at specific times, like a tour.
            Others allow customers to use their ticket anytime within a certain
            amount of time, like a 2-day city pass.
          </div>

          <div className={Classname.title}>
            Which best describes your activity?
          </div>
          {/* Duration Option */}
          <CustomRadio
            value={"duration"}
            label="It lasts for a specific amount of time (duration)"
            description="Example: 3-hour guided tour"
            selected={editedAccessTime}
            onSelect={() => {
              setEditedAccessTime("duration");
              dispatch(
                setOptionsData({ ...optionsData, accessTime: "duration" })
              );
            }}
          />

          {/* Validity Option */}
          <CustomRadio
            value={"validity"}
            label="Customers can use their ticket anytime during a certain period (validity)"
            description="Example: museum tickets that can be used anytime during opening hours"
            selected={editedAccessTime}
            onSelect={() => {
              setEditedAccessTime("validity");
              dispatch(
                setOptionsData({ ...optionsData, accessTime: "validity" })
              );
            }}
          />

          <div className="">
            <div className={Classname.title}>Set Your Cut-off time</div>
            <div className="mt-5 mb-8">
              The cut-off time is the latest you accept new bookings before the
              activity starts.
            </div>
            {/* <div className={Classname.description}>
            Having a cut-off time of 30 minutes or fewer can lead to 50% more
            bookings.
          </div> */}
            <div className={Classname.title}>
              How far in advance do you stop accepting new bookings before the
              activity starts?
            </div>
            <div className="w-80 mb-4">
              <FormControl fullWidth>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={editedCutoff}
                  onChange={(e) => {
                    setEditedCutoff(e.target.value);
                    dispatch(
                      setOptionsData({
                        ...optionsData,
                        cutOffTime: e.target.value,
                      })
                    );
                  }}
                >
                  {cutoffOptions?.map((option, index) => (
                    <MenuItem value={option} key={index}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            {/* <InfoCardLightening
            description={
              <div className="flex flex-col gap-1">
                <span>
                Setting a lower cut-off time can capture last minute bookings and drive more sales for your product.
                </span>
               
              </div>
            }
          /> */}
          </div>

          {/* TODO: MATERIAL UI DROPDOWN */}
          <div className={Classname.buttonsContainer}>
            <button className={Classname.saveButton} onClick={onSave}>
              Save and exit
            </button>
            {!tourEdit && (
              <button className={Classname.continueButton} onClick={onContinue}>
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default TourManagementPackageOptionsSetupPage;

const Classname = {
  container:
    "flex w-full min-h-screen relative bg-white flex-col items-center font-main",
  content:
    "flex flex-col lg:flex-row w-full max-w-7xl flex-1 pt-10 lg:pt-0 px-3 max-lg:px-4",
  detail: "flex flex-col py-10 lg:px-6 w-full",
  title: "font-bold text-lg mt-6 mb-2",
  input: "flex  h-14 border border-gray-400 rounded-md px-4 mt-2",
  textarea:
    "flex w-full h-44 py-4 border border-gray-400 rounded-md px-4 mt-2 mb-6",
  description: "flex text-base mb-4 mt-2",
  buttonsContainer: "flex flex-1 justify-between lg:justify-end gap-3 mt-10",
  optionButton:
    "font-bold w-fit flex items-center gap-2 cursor-pointer text-primary1 py-3 px-3 border rounded-lg",
  saveButton:
    "h-12 px-6 flex items-center justify-center font-bold text-gray-700 border border-gray-500 rounded-md",
  continueButton:
    "h-12 px-6 flex items-center justify-center text-white font-bold bg-primary1 rounded-md",
  listContainer: "flex flex-wrap gap-4 mt-4",
  listItem:
    "text-primary1 text-sm border border-primary1 px-3 py-1 rounded-md cursor-pointer flex items-center gap-2",
  addButton:
    "bg-primary h-14 px-4 rounded-lg flex items-center justify-center text-white font-bold mt-2",
};
